import React from 'react';
import { TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { cn, formatNumber, getChangeColor } from '../utils';
import { FuturesData } from '../services/marketData';

interface AssetsListProps {
  futuresData: FuturesData[];
}

export const AssetsList: React.FC<AssetsListProps> = ({ futuresData }) => {
  if (futuresData.length === 0) {
    return (
      <div className="glass-card p-6">
        <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-blue-400" />
          Complete Assets List
        </h2>
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400 mx-auto mb-4"></div>
          <p className="text-gray-400">Loading assets data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="glass-card p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-white flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-blue-400" />
          Complete Assets List
        </h2>
        <div className="text-sm text-gray-400">
          {futuresData.length} Assets
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {futuresData.map((asset, index) => {
          const isPositive = asset.changePercent > 0;
          const Icon = isPositive ? ArrowUpRight : ArrowDownRight;
          const sentimentColor = asset.sentiment === 'Bullish' ? 'text-green-400' : 
                               asset.sentiment === 'Bearish' ? 'text-red-400' : 'text-yellow-400';
          const sentimentBg = asset.sentiment === 'Bullish' ? 'bg-green-500/20 border-green-500/40' : 
                            asset.sentiment === 'Bearish' ? 'bg-red-500/20 border-red-500/40' : 'bg-yellow-500/20 border-yellow-500/40';
          
          return (
            <div key={index} className="bg-white/5 hover:bg-white/10 rounded-xl p-4 transition-all duration-200 hover:scale-105 border border-white/10">
              {/* Header with Symbol */}
              <div className="flex items-center justify-between mb-3">
                <div className={cn(
                  "w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-sm border-2",
                  sentimentBg
                )}>
                  {asset.symbol.length <= 3 ? asset.symbol : asset.symbol.substring(0, 3)}
                </div>
                <div className={cn("text-xs px-2 py-1 rounded-full font-medium", sentimentColor, sentimentBg)}>
                  {asset.sentiment}
                </div>
              </div>
              
              {/* Asset Name and Symbol */}
              <div className="mb-3">
                <h3 className="text-white font-semibold text-sm mb-1">{asset.name}</h3>
                <p className="text-gray-400 text-xs">{asset.symbol}</p>
              </div>
              
              {/* Price and Change */}
              <div className="mb-3">
                <p className="text-white font-bold text-lg mb-1">
                  {asset.symbol.includes('JPY') ? asset.price.toFixed(2) : 
                   asset.symbol.includes('DXY') ? asset.price.toFixed(2) :
                   asset.price < 10 ? asset.price.toFixed(4) : formatNumber.currency(asset.price)}
                </p>
                <div className={cn(
                  "flex items-center gap-1 text-sm font-medium",
                  getChangeColor(asset.change)
                )}>
                  <Icon className="w-4 h-4" />
                  <span>{isPositive ? '+' : ''}{asset.change.toFixed(4)}</span>
                  <span>({formatNumber.percentage(Math.abs(asset.changePercent))})</span>
                </div>
              </div>
              
              {/* Additional Info */}
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="text-center">
                  <p className="text-gray-400 mb-1">Volume</p>
                  <p className="text-white font-medium">{asset.volume ? formatNumber.compact(asset.volume) : 'N/A'}</p>
                </div>
                <div className="text-center">
                  <p className="text-gray-400 mb-1">Updated</p>
                  <p className="text-white font-medium">
                    {new Date(asset.lastUpdated).toLocaleTimeString('en-US', {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
              </div>
              
              {/* Market Category */}
              <div className="mt-3 pt-3 border-t border-white/10">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-400">
                    {asset.symbol.includes('USD') || asset.symbol.includes('EUR') || asset.symbol.includes('GBP') || asset.symbol.includes('JPY') || asset.symbol.includes('CHF') || asset.symbol.includes('AUD') || asset.symbol.includes('NZD') || asset.symbol.includes('CAD') || asset.symbol.includes('DXY') ? 'Currency' :
                     asset.symbol.includes('GOLD') || asset.symbol.includes('SILVER') || asset.symbol.includes('COPPER') || asset.symbol.includes('PLATINUM') ? 'Metals' :
                     asset.symbol.includes('OIL') || asset.symbol.includes('GAS') || asset.symbol.includes('GASOLINE') ? 'Energy' :
                     'Indices'}
                  </span>
                  <div className={cn(
                    "w-2 h-2 rounded-full",
                    isPositive ? 'bg-green-400' : 'bg-red-400'
                  )}></div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      
      {/* Summary Stats */}
      <div className="mt-6 pt-6 border-t border-white/10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div>
            <p className="text-2xl font-bold text-green-400">
              {futuresData.filter(asset => asset.changePercent > 0).length}
            </p>
            <p className="text-xs text-gray-400">Rising</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-red-400">
              {futuresData.filter(asset => asset.changePercent < 0).length}
            </p>
            <p className="text-xs text-gray-400">Falling</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-green-400">
              {futuresData.filter(asset => asset.sentiment === 'Bullish').length}
            </p>
            <p className="text-xs text-gray-400">Bullish</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-red-400">
              {futuresData.filter(asset => asset.sentiment === 'Bearish').length}
            </p>
            <p className="text-xs text-gray-400">Bearish</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssetsList;